/** Common master setup order — sidebar Master Data only (service masters live in hub sub-services) */

export interface MasterSetupStep {
  groupKey: string;
  typeKey: string;
  label: string;
  why: string;
  example?: string;
  tips?: string[];
  externalLink?: string;
  modules?: string[];
}

export interface MasterSetupPhase {
  id: string;
  title: string;
  subtitle: string;
  color: string;
  modules?: string[];
  steps: MasterSetupStep[];
}

export const MASTER_SETUP_PHASES: MasterSetupPhase[] = [
  {
    id: 'foundation',
    title: '1. Geography',
    subtitle: 'Countries — used in zone management and all services',
    color: '#2563eb',
    steps: [
      { groupKey: 'geo', typeKey: 'countries', label: 'Countries', why: 'Pre-seeded globally — review locale, tax, and currency defaults' },
    ],
  },
  {
    id: 'locale',
    title: '2. Locale & Currency',
    subtitle: 'Languages, currencies, timezones, tax regions',
    color: '#7c3aed',
    steps: [
      { groupKey: 'locale', typeKey: 'languages', label: 'Languages', why: 'Supported locales per tenant' },
      { groupKey: 'locale', typeKey: 'currencies', label: 'Currencies', why: 'Multi-currency support' },
      { groupKey: 'locale', typeKey: 'timezones', label: 'Timezones', why: 'Tenant timezone configuration' },
      { groupKey: 'locale', typeKey: 'taxes', label: 'Tax Rules', why: 'GST / VAT configuration' },
    ],
  },
  {
    id: 'operations',
    title: '3. Operations',
    subtitle: 'Shared rejection reasons, delivery modes, address fields',
    color: '#0369a1',
    steps: [
      { groupKey: 'operations', typeKey: 'rejection_reasons', label: 'Rejection Reasons', why: 'Generic reject codes' },
      { groupKey: 'operations', typeKey: 'delivery_modes', label: 'Delivery Modes', why: 'Standard, express, scheduled' },
      { groupKey: 'operations', typeKey: 'address_fields', label: 'Address Fields', why: 'Address capture — district/locality from Google Places, not masters' },
    ],
  },
  {
    id: 'payments',
    title: '4. Payments',
    subtitle: 'Payment methods, banks, payout cycles',
    color: '#16a34a',
    steps: [
      { groupKey: 'payments', typeKey: 'payment_methods', label: 'Payment Methods', why: 'Cash, card, UPI, wallet' },
      { groupKey: 'payments', typeKey: 'banks', label: 'Banks', why: 'Settlement bank master' },
      { groupKey: 'payments', typeKey: 'payout_cycles', label: 'Payout Cycles', why: 'Settlement frequency' },
    ],
  },
];

/** Geo field → master type used to populate dropdown options */
export const GEO_FIELD_SOURCE: Partial<Record<string, string>> = {
  country: 'countries',
};

/** Parent master type for hierarchical records (23 Cabs: Vehicle → Brand → Model → Variant) */
export const HIERARCHY_PARENT_TYPE: Partial<Record<string, string>> = {
  vehicle_brands: 'vehicle_types',
  vehicle_models: 'vehicle_brands',
  vehicle_variants: 'vehicle_models',
  food_categories: 'food_categories',
  food_sub_categories: 'food_categories',
  food_attribute_values: 'food_attributes',
  ride_attribute_values: 'ride_attributes',
  categories: 'categories',
  service_categories: 'service_categories',
};

export function filterSetupPhases(enabledModules: string[], isSuperAdmin: boolean): MasterSetupPhase[] {
  return MASTER_SETUP_PHASES.map((phase) => ({
    ...phase,
    steps: phase.steps.filter((step) => {
      if (!step.modules?.length || isSuperAdmin) return true;
      return step.modules.some((m) => enabledModules.includes(m));
    }),
  })).filter((phase) => phase.steps.length > 0);
}
